// Generic ZDR agent-chat modal — the shared form factor behind the Agreement Queue and
// the What's New chat. Stateless: holds the transcript, re-sends it each turn via `ask`.
// Airy line-free tables, markdown links, shorthand answers, cursor stays in the bar.
// (Ken 2026-10-12)
import { useState, useRef, useEffect } from 'react'
import { X, Send } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Components } from 'react-markdown'

type Msg = { role: 'user' | 'assistant'; content: string }
const USER_BG = 'var(--me-accent-bg)'  // fill token (Ken 2026-10-22 D); studio-only modal

const MD: Components = {
  table: (p) => <table className="me-aq-table" style={{ borderCollapse: 'collapse', width: '100%', margin: '2px 0' }} {...p} />,
  th: (p) => <th style={{ textAlign: 'left', fontWeight: 600, padding: '2px 14px 6px 0', verticalAlign: 'top', color: 'var(--me-textMute)', borderBottom: '1px solid var(--me-border)' }} {...p} />,
  td: (p) => <td style={{ textAlign: 'left', padding: '6px 14px 6px 0', verticalAlign: 'top' }} {...p} />,
  a: (p) => <a style={{ color: 'var(--me-accent-fg)', textDecoration: 'underline' }} {...p} />,
}

export default function AgentChatModal(
  { title, subtitle, suggestions, ask, onClose }:
  { title: string; subtitle: string; suggestions: string[]
    ask: (messages: Msg[]) => Promise<string>; onClose: () => void },
) {
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight) }, [messages, loading])

  const send = async (text: string) => {
    const q = text.trim()
    if (!q || loading) return
    const next = [...messages, { role: 'user' as const, content: q }]
    setMessages(next)
    setInput('')
    setLoading(true)
    try {
      setMessages([...next, { role: 'assistant', content: await ask(next) }])
    } catch {
      setMessages([...next, { role: 'assistant', content: '_Unavailable right now._' }])
    } finally {
      setLoading(false)
      inputRef.current?.focus()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.45)' }} onClick={onClose}>
      <div className="w-full max-w-4xl flex flex-col rounded-xl overflow-hidden"
        style={{ background: 'var(--me-panel)', border: '1px solid var(--me-border)', height: '85vh' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--me-border)' }}>
          <div>
            <div className="text-[15px] font-semibold me-text">{title}</div>
            <div className="text-[12px] me-text-mute">{subtitle}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[color:var(--me-hover)]" aria-label="Close">
            <X size={18} style={{ color: 'var(--me-textMute)' }} />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-3">
              <div className="text-[13px] me-text-mute">Try one of these:</div>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => send(s)}
                    className="rounded-md px-2.5 py-1 text-[12.5px]"
                    style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            m.role === 'user' ? (
              <div key={i} className="flex justify-end">
                <div className="rounded-lg px-3 py-2 text-[13.5px] whitespace-pre-wrap"
                  style={{ background: USER_BG, color: '#fff', maxWidth: '80%' }}>
                  {m.content}
                </div>
              </div>
            ) : (
              <div key={i} className="me-prose me-text break-words text-[13.5px]" style={{ maxWidth: '100%' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{m.content}</ReactMarkdown>
              </div>
            )
          ))}
          {loading && <div className="text-[13px] me-text-mute">…</div>}
        </div>

        <div className="flex items-center gap-2 px-3 py-3" style={{ borderTop: '1px solid var(--me-border)' }}>
          <input ref={inputRef} autoFocus value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(input) }}
            placeholder="Ask…" className="flex-1 rounded-md px-3 py-2 text-[13.5px]"
            style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }} />
          <button onClick={() => send(input)} disabled={loading || !input.trim()}
            className="rounded-md px-3 py-2 flex items-center gap-1 text-[13px] font-medium"
            style={{ background: USER_BG, color: '#fff', opacity: loading || !input.trim() ? 0.5 : 1 }}>
            <Send size={14} /> Send
          </button>
        </div>
      </div>
    </div>
  )
}
